<script setup>
import { useAbility } from '@casl/vue'

import { cookieRef } from '@/@layouts/stores/config'

import DocumentService from '@/services/quality/document.service'
import DocumentTypeService from '@/services/quality/documentType.service'
import { DocumentStatus, documentStatusTitle } from '@/enums/quality/document-status'
import { DocumentTier, documentTierTitle } from '@/enums/quality/document-tier'
import DocumentFacetRail from '@/views/quality/document/DocumentFacetRail.vue'
import DocumentCard from '@/views/quality/document/DocumentCard.vue'
import DocumentTable from '@/views/quality/document/DocumentTable.vue'
import DocumentUploadDrawer from '@/views/quality/document/DocumentUploadDrawer.vue'
import DocumentCreateStepper from '@/views/quality/document/DocumentCreateStepper.vue'
import DocumentWithdrawDialog from '@/views/quality/document/DocumentWithdrawDialog.vue'

definePage({
  meta: {
    name: 'app-quality-document',
    subject: 'document.menu',
    action: 'register',
  },
})

const router = useRouter()
const { can } = useAbility()

const selectedUnitId = cookieRef('selectedUnit')

// ── View mode ────────────────────────────────────────
// No grid/list toggle exists anywhere in this codebase, so this is built here.
// Persisted per user so the choice survives a reload — a quality manager who
// prefers the table should not re-pick it every visit.
const viewMode = cookieRef('documentViewMode', 'grid')

// ── Facets ───────────────────────────────────────────
const facet = ref('department')
const facetNodes = ref([])
const facetLoading = ref(false)
const selectedFacet = ref({ parent: null, child: null, label: null })

// Monotonic token guarding against out-of-order tree responses: toggling
// Department → Type → Standard quickly fires three independent requests with
// no cancellation, and a slower earlier one could otherwise land last and
// overwrite facetNodes with the wrong facet's data while the toggle shows the
// newer selection. A separate counter from fetchToken — the two requests are
// independent.
let facetToken = 0

const loadFacets = async () => {
  const token = ++facetToken

  facetLoading.value = true
  await DocumentService.tree(facet.value, data => {
    // Ignore a tree a newer facet selection has already superseded.
    if (token !== facetToken) return
    facetNodes.value = data?.data ?? data ?? []
  })
  if (token === facetToken) facetLoading.value = false
}

watch(facet, () => {
  selectedFacet.value = { parent: null, child: null, label: null }
  loadFacets()
})

// ── Filters ──────────────────────────────────────────
const documentTypeId = ref(null)
const tier = ref(null)
const status = ref(null)
const overdue = ref(false)
const searchQuery = ref('')

const debouncedSearch = useDebounceFn(val => {
  searchQuery.value = val
  page.value = 1
}, 500)

const documentTypes = ref([])

DocumentTypeService.list({ unit_id: selectedUnitId.value }, data => {
  documentTypes.value = data?.data ?? data ?? []
})

const statusOptions = Object.values(DocumentStatus).map(value => ({ title: documentStatusTitle(value), value }))
const tierOptions = Object.values(DocumentTier).map(value => ({ title: documentTierTitle(value), value }))

// The rail and these two dropdowns write the SAME query keys (tier,
// document_type_id) when the active facet is Type. Letting both stand would
// build a silently contradictory query (e.g. rail says "Policy › SOP" while
// the dropdown says "Consent Form") with the breadcrumb still showing the old
// path. Disable the dropdown for whichever dimension the rail already
// constrains, with a visible reason, rather than dropping either value.
const isTierDropdownLocked = computed(() => facet.value === 'type' && !!selectedFacet.value.parent)
const isTypeDropdownLocked = computed(() => facet.value === 'type' && !!selectedFacet.value.child)
const facetLockHint = 'Filtered by the folder you are in'

// The lock must decide the QUERY, not merely the control's disabled state. A
// locked dropdown that still won in buildQuery/filters made the hint a lie:
// it claimed the folder was filtering while a STALE dropdown value (set
// before the facet was switched to Type, and never cleared by watch(facet))
// actually drove the request. Reachable via set-then-drill: set Tier while on
// the Department facet (correctly enabled there), switch to Type (tier isn't
// reset), then drill into a tier folder — the dropdown renders disabled but
// its stale value used to still win. Do NOT clear tier/documentTypeId on
// facet change instead — that would make a filter vanish with no
// explanation, which the previous fix round explicitly ruled out. Preserving
// the value means it simply applies again once the folder selection clears.
const effectiveTier = computed(() => (isTierDropdownLocked.value ? null : tier.value))
const effectiveTypeId = computed(() => (isTypeDropdownLocked.value ? null : documentTypeId.value))

// The rail's null buckets arrive as the string NULL_SENTINEL ('__null__'), not
// '' — truthy, so `child || undefined` passes it through as a query value.
// The backend translates that sentinel to `whereNull('department_id')` for
// department_id, but to `whereNull('section') OR section = ''` for section —
// they are different columns with different null semantics, not the same
// IS NULL check twice. Do not "simplify" this to a nullish check; '' was the
// bug. uploadPrefill below reads the same selectedFacet keys and must also
// treat NULL_SENTINEL as null, not as literal text.
const NULL_SENTINEL = '__null__'

// The facet selection is a filter, not a separate query path — selecting
// "Nursing › ICU" is exactly department_id=N & section=ICU.
const facetFilters = computed(() => {
  const { parent, child } = selectedFacet.value

  if (!parent) return {}

  if (facet.value === 'department')
    return { department_id: parent, section: child || undefined }

  if (facet.value === 'type')
    return { tier: parent, document_type_id: child || undefined }

  // standard_PREFIX, not standard_ref. The rail's child node is a STANDARD
  // ("MOI.02.00") while a document tags a measurable element ("MOI.02.00 ME1"),
  // because JCI bakes the ME into the code. standard_ref is an exact
  // whereJsonContains match and would return an empty list for every folder.
  //
  // A child node is a whole standard ("MOI.02.00"); a parent is a chapter
  // ("MOI"). They are different filters — the chapter is not a prefix the
  // standard_prefix leg can match, so sending it there returned zero documents
  // for every chapter click.
  return child
    ? { standard_prefix: child }
    : { standard_chapter: parent }
})

const filters = computed(() => ({
  ...facetFilters.value,
  document_type_id: effectiveTypeId.value ?? facetFilters.value.document_type_id,
  tier: effectiveTier.value ?? facetFilters.value.tier,
  status: status.value,
  overdue: overdue.value,
  search: searchQuery.value,
}))

// ── Data ─────────────────────────────────────────────
const documents = ref([])
const total = ref(0)
const isFetching = ref(false)
const page = ref(1)
const itemsPerPage = ref(24)

const buildQuery = () => ({
  ...facetFilters.value,
  document_type_id: effectiveTypeId.value || facetFilters.value.document_type_id || undefined,
  tier: effectiveTier.value || facetFilters.value.tier || undefined,
  status: status.value || undefined,
  overdue: overdue.value || undefined,
  search: searchQuery.value || undefined,
  per_page: itemsPerPage.value,
  page: page.value,
})

// Monotonic token guarding against out-of-order responses: filter changes,
// search and pagination each fire an independent request with no cancellation,
// so a slow earlier response could otherwise overwrite correct rows with stale
// ones. Copied from the risk register's list page.
let fetchToken = 0

const fetchDocuments = async () => {
  const token = ++fetchToken

  isFetching.value = true
  await DocumentService.list(buildQuery(), data => {
    if (token !== fetchToken) return
    documents.value = data?.data ?? []
    total.value = data?.meta?.total ?? 0
  })
  if (token === fetchToken) isFetching.value = false
}

// `filters` is a computed returning a fresh object of primitives on every
// evaluation, so reference comparison already fires — no { deep: true }
// needed (matches the risk register's equivalent watcher).
watch(filters, () => {
  if (page.value === 1) fetchDocuments()
  else page.value = 1
})

watch([page, itemsPerPage], fetchDocuments)

fetchDocuments()
loadFacets()

const onSelectFacet = selection => {
  selectedFacet.value = selection
}

// ── Breadcrumbs ──────────────────────────────────────
// A Drive-like browser needs the crumb trail to be a way back out, not just a
// label: "All documents" clears the facet selection, and an intermediate
// parent crumb drops back to the parent-only selection. Only the deepest
// (current) crumb is inert.
const breadcrumbs = computed(() => {
  const { parent, label } = selectedFacet.value

  const crumbs = [{
    title: 'All documents',
    disabled: !parent,
    onClick: () => onSelectFacet({ parent: null, child: null, label: null }),
  }]

  if (label) {
    const parts = label.split('›').map(part => part.trim())

    parts.forEach((part, index) => {
      const isDeepest = index === parts.length - 1

      crumbs.push({
        title: part,
        disabled: isDeepest,
        onClick: isDeepest ? undefined : () => onSelectFacet({ parent, child: null, label: parts[0] }),
      })
    })
  }

  return crumbs
})

// ── Create flows ─────────────────────────────────────
const isUploadOpen = ref(false)
const isStepperOpen = ref(false)

// Spec §10.3: a drop lands in the CURRENT folder, pre-filling department and
// section — the folder is not decoration, it is the first two form fields.
const uploadPrefill = computed(() => {
  const { parent, child } = selectedFacet.value

  if (facet.value !== 'department' || !parent) return {}

  return {
    department_id: parent === NULL_SENTINEL ? null : Number(parent),
    section: child === NULL_SENTINEL ? null : child,
  }
})

const openUpload = () => { isUploadOpen.value = true }

const onCreated = () => {
  isUploadOpen.value = false
  isStepperOpen.value = false
  fetchDocuments()
  loadFacets()
}

// ── Row actions ──────────────────────────────────────
const withdrawTarget = ref(null)
const isWithdrawOpen = ref(false)

const goToDocument = document => {
  router.push({ name: 'app-quality-document-id', params: { id: document.id } })
}

const onRowAction = async ({ key, document }) => {
  if (key === 'revise' || key === 'download') {
    // Both need the version context that only the detail page holds.
    goToDocument(document)

    return
  }

  if (key === 'withdraw') {
    withdrawTarget.value = document
    isWithdrawOpen.value = true
  }
}
</script>

<template>
  <div>
    <div class="d-flex align-center flex-wrap gap-4 mb-6">
      <div class="flex-grow-1">
        <div class="text-h5">
          Documents
        </div>
        <VBreadcrumbs
          :items="breadcrumbs"
          class="pa-0 text-body-2"
        >
          <template #title="{ item }">
            <span
              :class="{ 'cursor-pointer': item.onClick }"
              @click="item.onClick && item.onClick()"
            >{{ item.title }}</span>
          </template>
        </VBreadcrumbs>
      </div>

      <VBtn
        v-if="can('create', 'quality.document')"
        prepend-icon="tabler-upload"
        @click="openUpload"
      >
        Upload
      </VBtn>
      <VBtn
        v-if="can('create', 'quality.document')"
        variant="tonal"
        prepend-icon="tabler-plus"
        @click="isStepperOpen = true"
      >
        New document
      </VBtn>
    </div>

    <VRow>
      <VCol
        cols="12"
        md="3"
      >
        <DocumentFacetRail
          v-model:facet="facet"
          :nodes="facetNodes"
          :loading="facetLoading"
          :selected="selectedFacet"
          @select="onSelectFacet"
        />
      </VCol>

      <VCol
        cols="12"
        md="9"
      >
        <VCard
          variant="flat"
          class="border mb-4"
        >
          <VCardText class="d-flex align-center flex-wrap gap-3">
            <AppTextField
              placeholder="Search code, title or purpose..."
              density="compact"
              style="inline-size: 260px;"
              @input="debouncedSearch($event.target.value)"
            />
            <AppSelect
              v-model="documentTypeId"
              :items="documentTypes"
              item-title="name"
              item-value="id"
              placeholder="Type"
              density="compact"
              clearable
              :disabled="isTypeDropdownLocked"
              :hint="isTypeDropdownLocked ? facetLockHint : ''"
              persistent-hint
              style="inline-size: 170px;"
            />
            <AppSelect
              v-model="status"
              :items="statusOptions"
              placeholder="Status"
              density="compact"
              clearable
              style="inline-size: 150px;"
            />
            <AppSelect
              v-model="tier"
              :items="tierOptions"
              placeholder="Tier"
              density="compact"
              clearable
              :disabled="isTierDropdownLocked"
              :hint="isTierDropdownLocked ? facetLockHint : ''"
              persistent-hint
              style="inline-size: 190px;"
            />
            <VCheckbox
              v-model="overdue"
              label="Review overdue"
              density="compact"
              hide-details
            />

            <VSpacer />

            <VBtnToggle
              v-model="viewMode"
              density="compact"
              variant="outlined"
              divided
              mandatory
            >
              <VBtn
                value="grid"
                size="small"
              >
                <VIcon icon="tabler-layout-grid" />
              </VBtn>
              <VBtn
                value="list"
                size="small"
              >
                <VIcon icon="tabler-list" />
              </VBtn>
            </VBtnToggle>
          </VCardText>
        </VCard>

        <template v-if="viewMode === 'grid'">
          <VProgressLinear
            v-if="isFetching"
            indeterminate
            color="primary"
            class="mb-3"
          />
          <VRow v-if="documents.length">
            <VCol
              v-for="document in documents"
              :key="document.id"
              cols="12"
              sm="6"
              lg="4"
            >
              <DocumentCard
                :document="document"
                @open="goToDocument"
                @action="onRowAction"
              />
            </VCol>
          </VRow>
          <VCard
            v-else-if="!isFetching"
            variant="flat"
            class="border"
          >
            <VCardText class="text-center py-12">
              <VIcon
                icon="tabler-folder-open"
                size="48"
                class="text-disabled mb-3"
              />
              <div class="text-body-1 text-medium-emphasis">
                No documents here yet.
              </div>
            </VCardText>
          </VCard>

          <div
            v-if="total > itemsPerPage"
            class="d-flex justify-center mt-6"
          >
            <VPagination
              v-model="page"
              :length="Math.ceil(total / itemsPerPage) || 1"
              :total-visible="5"
            />
          </div>
        </template>

        <DocumentTable
          v-else
          v-model:page="page"
          v-model:items-per-page="itemsPerPage"
          :documents="documents"
          :total="total"
          :loading="isFetching"
          @open="goToDocument"
          @action="onRowAction"
        />
      </VCol>
    </VRow>

    <DocumentUploadDrawer
      v-model:is-drawer-open="isUploadOpen"
      :prefill="uploadPrefill"
      @saved="onCreated"
    />

    <DocumentCreateStepper
      v-model:is-dialog-open="isStepperOpen"
      :prefill="uploadPrefill"
      @saved="onCreated"
    />

    <DocumentWithdrawDialog
      v-model:is-dialog-open="isWithdrawOpen"
      :document="withdrawTarget"
      @withdrawn="onCreated"
    />
  </div>
</template>
